import { useState, useMemo } from "react";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { nutritionAPI, type FoodSource } from "@/lib/nutrition-api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, Loader2, Search } from "lucide-react";
import { toast } from "sonner";

export const FoodSources = () => {
  const queryClient = useQueryClient();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [sourceName, setSourceName] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  const { data: sources, isLoading } = useQuery({
    queryKey: ["food-sources"],
    queryFn: () => nutritionAPI.foodSources.list(),
  });

  const createMutation = useMutation({
    mutationFn: (name: string) => nutritionAPI.foodSources.create({ name }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["food-sources"] });
      toast.success("Food source added successfully");
      setSourceName("");
      setIsDialogOpen(false);
    },
    onError: (error: any) => {
      toast.error(error?.message || "Failed to add food source");
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!sourceName.trim()) {
      toast.error("Please enter a source name");
      return;
    }
    createMutation.mutate(sourceName);
  };

  const handleSearch = () => {
    setSearchQuery(searchInput);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const filteredSources = useMemo(() => {
    if (!sources) return [];
    if (!searchQuery.trim()) return sources;
    const q = searchQuery.toLowerCase();
    return sources.filter((s) => s.name.toLowerCase().includes(q));
  }, [sources, searchQuery]);

  const totalCount = sources?.length ?? 0;

  return (
    <div className="h-full flex flex-col gap-4">
      <Card className="flex-1 flex flex-col min-h-0">
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle className="z-h2">Food Sources</CardTitle>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button size="sm" className="gap-2">
                <Plus className="h-4 w-4" />
                Add Source
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add Food Source</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="sourceName">Source Name</Label>
                  <Input
                    id="sourceName"
                    value={sourceName}
                    onChange={(e) => setSourceName(e.target.value)}
                    placeholder="Enter source name"
                    disabled={createMutation.isPending}
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsDialogOpen(false)}
                    disabled={createMutation.isPending}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={createMutation.isPending}>
                    {createMutation.isPending && (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    )}
                    Add Source
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent className="flex-1 flex flex-col overflow-hidden">
          <div className="flex gap-2 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search food sources by name..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyPress={handleKeyPress}
                className="pl-10"
              />
            </div>
            <Button onClick={handleSearch} disabled={isLoading}>
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                "Search"
              )}
            </Button>
          </div>

          <div className="mb-3 z-caption text-muted-foreground">
            Showing {filteredSources.length} of {totalCount} source
            {totalCount !== 1 ? "s" : ""}
            {searchQuery && ` for "${searchQuery}"`}
          </div>

          <div className="border rounded-lg flex-1 overflow-auto">
            <Table>
              <TableHeader className="sticky top-0 bg-card z-10">
                <TableRow>
                  <TableHead className="w-[80px] z-eyebrow text-muted-foreground">
                    ID
                  </TableHead>
                  <TableHead className="z-eyebrow text-muted-foreground">
                    Source Name
                  </TableHead>
                  <TableHead className="z-eyebrow text-muted-foreground">
                    Created At
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={3} className="text-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin mx-auto" />
                      <p className="z-caption text-muted-foreground mt-2">
                        Loading sources...
                      </p>
                    </TableCell>
                  </TableRow>
                ) : filteredSources.length > 0 ? (
                  filteredSources.map((source: FoodSource) => (
                    <TableRow key={source.id} className="hover:bg-muted/30">
                      <TableCell className="z-caption text-muted-foreground tabular-nums">
                        {source.id}
                      </TableCell>
                      <TableCell className="z-body">{source.name}</TableCell>
                      <TableCell className="z-caption text-muted-foreground">
                        {new Date(source.created_at).toLocaleDateString(
                          "en-US",
                          {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                          }
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={3}
                      className="text-center py-8 text-muted-foreground"
                    >
                      {searchQuery
                        ? "No food sources match your search."
                        : "No food sources found. Add one to get started."}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
